import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  type NotificationRule,
  fetchNotificationTypes,
  fetchNotificators,
  createNotification,
  updateNotification,
} from '@/lib/traccar-api';

interface NotificationRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: NotificationRule;
}

const getNotificatorLabel = (type: string, t: (key: string) => string) =>
  type === 'firebase' ? t('notifications.config.mobileApp') : type;

const HIDDEN_NOTIFICATION_TYPES = new Set(['queuedCommandSent']);

// Channels not offered when creating or editing a rule.
const HIDDEN_NOTIFICATORS = new Set(['command']);

export function NotificationRuleDialog({ open, onOpenChange, rule }: NotificationRuleDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [type, setType] = useState('');
  const [selectedNotificators, setSelectedNotificators] = useState<string[]>([]);
  const [minIdleMinutes, setMinIdleMinutes] = useState<string>('');
  const [speedLimitKmh, setSpeedLimitKmh] = useState<string>('');

  useEffect(() => {
    if (open) {
      setType(rule?.type ?? '');
      setSelectedNotificators(rule?.notificators ? rule.notificators.split(',').filter(Boolean) : []);
      const minIdle = rule?.attributes?.minIdlePeriod as number | undefined;
      setMinIdleMinutes(minIdle ? String(Math.round(minIdle / 60)) : '');
      // Traccar stores speedLimit in knots
      const speedLimit = rule?.attributes?.speedLimit as number | undefined;
      setSpeedLimitKmh(speedLimit ? String(Math.round(speedLimit * 1.852)) : '');
    }
  }, [open, rule]);

  const KNOWN_TYPES = [
    'alarm', 'commandResult', 'deviceFuelDrop', 'deviceFuelIncrease',
    'deviceIdle', 'deviceInactive', 'deviceMoving', 'deviceOffline',
    'deviceOnline', 'deviceOverspeed', 'deviceStopped', 'deviceUnknown',
    'driverChanged', 'geofenceEnter', 'geofenceExit', 'ignitionOff',
    'ignitionOn', 'maintenance', 'media', 'queuedCommandSent', 'test', 'textMessage',
  ];

  const { data: apiTypes = [] } = useQuery({
    queryKey: ['notificationTypes'],
    queryFn: fetchNotificationTypes,
  });

  const types = [
    ...apiTypes,
    ...KNOWN_TYPES
      .filter((k) => !apiTypes.some((t) => t.type === k))
      .map((k) => ({ type: k })),
  ].filter((nt) => !HIDDEN_NOTIFICATION_TYPES.has(nt.type));

  const { data: allNotificators = [] } = useQuery({
    queryKey: ['notificators'],
    queryFn: fetchNotificators,
  });
  const notificators = allNotificators.filter((n) => !HIDDEN_NOTIFICATORS.has(n.type));

  const mutation = useMutation({
    mutationFn: (data: Omit<NotificationRule, 'id'>) =>
      rule ? updateNotification({ ...data, id: rule.id }) : createNotification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success(rule ? t('notifications.config.updated') : t('notifications.config.created'));
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const attributes: Record<string, unknown> = { ...(rule?.attributes ?? {}) };
    if (type === 'deviceIdle' && minIdleMinutes) {
      attributes.minIdlePeriod = parseInt(minIdleMinutes, 10) * 60;
    } else {
      delete attributes.minIdlePeriod;
    }
    if (type === 'deviceOverspeed' && speedLimitKmh) {
      attributes.speedLimit = parseFloat(speedLimitKmh) / 1.852;
    } else {
      delete attributes.speedLimit;
    }
    mutation.mutate({
      type,
      always: true,
      notificators: selectedNotificators.join(','),
      calendarId: rule?.calendarId ?? 0,
      attributes,
    });
  };

  const toggleNotificator = (notificatorType: string) => {
    setSelectedNotificators((prev) =>
      prev.includes(notificatorType)
        ? prev.filter((n) => n !== notificatorType)
        : [...prev, notificatorType]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {rule ? t('notifications.config.editTitle') : t('notifications.config.createTitle')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">{t('notifications.config.type')}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="type">
                <SelectValue placeholder={t('notifications.config.type')} />
              </SelectTrigger>
              <SelectContent>
                {[...types]
                  .sort((a, b) =>
                    t(`notifications.events.${a.type}`, { defaultValue: a.type }).localeCompare(
                      t(`notifications.events.${b.type}`, { defaultValue: b.type })
                    )
                  )
                  .map((nt) => (
                    <SelectItem key={nt.type} value={nt.type}>
                      {t(`notifications.events.${nt.type}`, { defaultValue: nt.type })}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {type === 'deviceIdle' && (
            <div className="space-y-2">
              <Label htmlFor="minIdleMinutes">{t('notifications.config.idleTime')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="minIdleMinutes"
                  type="number"
                  min="1"
                  value={minIdleMinutes}
                  onChange={(e) => setMinIdleMinutes(e.target.value)}
                  placeholder="5"
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">{t('notifications.config.idleTimeUnit')}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('notifications.config.idleTimeDesc')}</p>
            </div>
          )}

          {type === 'deviceOverspeed' && (
            <div className="space-y-2">
              <Label htmlFor="speedLimitKmh">{t('notifications.config.maxSpeed')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="speedLimitKmh"
                  type="number"
                  min="1"
                  value={speedLimitKmh}
                  onChange={(e) => setSpeedLimitKmh(e.target.value)}
                  placeholder="80"
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">km/h</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('notifications.config.maxSpeedDesc')}</p>
            </div>
          )}

          {notificators.length > 0 && (
            <div className="space-y-2">
              <Label>{t('notifications.config.channels')}</Label>
              <div className="flex flex-wrap gap-4">
                {notificators.map((n) => (
                  <div key={n.type} className="flex items-center gap-2">
                    <Checkbox
                      id={`notificator-${n.type}`}
                      checked={selectedNotificators.includes(n.type)}
                      onCheckedChange={() => toggleNotificator(n.type)}
                    />
                    <Label htmlFor={`notificator-${n.type}`} className="capitalize cursor-pointer">
                      {getNotificatorLabel(n.type, t)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!type || mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {rule ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
